import { useEffect, useState } from "react";
import { api, AUD } from "../lib/api";
import { ArrowsClockwise, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isDemoMode } from "../lib/demoStore";

const CHIPS = {
    High: "status-on-plan",
    Medium: "status-watch",
    Low: "status-review",
};

export default function BalanceSheet() {
    const [items, setItems] = useState([]);
    const [entity, setEntity] = useState("All");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    // Personal holdings (Live Prices) — aggregated by owner for the summary
    // card. Keeps Balance Sheet as the single glance for total wealth.
    const [personalHoldings, setPersonalHoldings] = useState([]);

    const load = async () => {
        const [bs, h] = await Promise.all([
            api.get("/balance-sheet"),
            api.get("/holdings").catch(() => ({ data: [] })),
        ]);
        setItems(bs.data);
        setPersonalHoldings((h.data || []).filter(x => x.entity === "Personal"));
    };
    useEffect(() => { load(); }, []);

    const openSync = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/balance-sheet/sync-preview");
            if (!data.count) {
                toast.info("Balance sheet is already up to date with live prices.");
                return;
            }
            setPreview(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    };

    const applySync = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/balance-sheet/sync-from-live");
            toast.success(`Balance sheet updated — ${data.updated} portfolio(s) synced to live prices.`);
            setPreview(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sync failed");
        } finally { setBusy(false); }
    };

    const filtered = entity === "All" ? items : items.filter(i => i.entity === entity);
    const totals = items.reduce((acc, i) => {
        acc[i.entity] = (acc[i.entity] || 0) + Number(i.value || 0);
        return acc;
    }, {});
    const grand = Object.values(totals).reduce((a, b) => a + b, 0);
    // Derive entity list from data so demo profiles (with/without Family Business)
    // and the live app (with Pendaz) both work without hardcoding.
    const entityList = Object.keys(totals).sort((a, b) => {
        const order = ["Personal", "Family Business", "Pendaz", "SMSF"];
        const ai = order.indexOf(a), bi = order.indexOf(b);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return (
        <div>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Family Balance Sheet</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Assets by entity.</h1>
                </div>
                <button onClick={openSync} disabled={busy}
                        className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
                        data-testid="bs-sync-from-live">
                    <ArrowsClockwise size={14} />
                    {busy ? "Checking…" : "Sync from live prices"}
                </button>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-10">
                {entityList.map(e => (
                    <div key={e} className="hairline bg-white p-6 rounded" data-testid={`entity-total-${e.toLowerCase().replace(/\s+/g,'-')}`}>
                        <div className="label-eyebrow">{e}</div>
                        <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(totals[e] || 0)}</div>
                    </div>
                ))}
                <div className="p-6 rounded" style={{ background: "var(--eucalyptus)", color: "var(--bone)" }}
                     data-testid="entity-total-family">
                    <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>Family Total</div>
                    <div className="font-serif text-3xl mt-3 font-mono-num">{AUD(grand)}</div>
                </div>
            </div>

            {personalHoldings.length > 0 && (() => {
                const byOwner = personalHoldings.reduce((acc, h) => {
                    const mv = (Number(h.last_price) || 0) * (Number(h.units) || 0);
                    const k = h.owner || "Joint";
                    acc[k] = (acc[k] || 0) + mv;
                    return acc;
                }, {});
                const total = Object.values(byOwner).reduce((a, b) => a + b, 0);
                return (
                    <div className="hairline bg-white p-6 rounded mb-10" data-testid="personal-investments-card">
                        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                            <div>
                                <div className="label-eyebrow">Personal Investments</div>
                                <div className="text-sm text-[var(--muted)] mt-1">
                                    Live-priced shares held outside SMSF & Pendaz — {personalHoldings.length} line{personalHoldings.length === 1 ? "" : "s"}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="label-eyebrow">Live value</div>
                                <div className="font-serif text-3xl mt-1 font-mono-num"
                                     data-testid="personal-investments-total">{AUD(total)}</div>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-3">
                            {["Darren", "Penny", "Joint"].map(owner => {
                                const displayLabel = isDemoMode()
                                    ? (owner === "Darren" ? "Partner A" : owner === "Penny" ? "Partner B" : "Joint")
                                    : owner;
                                return (
                                    <div key={owner} className="p-3 rounded"
                                         style={{ background: "var(--line)40" }}
                                         data-testid={`personal-investments-${owner.toLowerCase()}`}>
                                        <div className="label-eyebrow">{displayLabel}</div>
                                        <div className="font-mono-num text-lg mt-2">{AUD(byOwner[owner] || 0)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="entity-filter">
                {["All", ...entityList].map(e => (
                    <button key={e}
                            onClick={() => setEntity(e)}
                            data-testid={`filter-${e.toLowerCase().replace(/\s+/g,'-')}`}
                            className={`px-4 py-1.5 rounded text-sm border ${entity === e
                                ? "bg-[var(--eucalyptus)] text-[var(--bone)] border-[var(--eucalyptus)]"
                                : "border-[var(--line)] hover:bg-black/5"}`}>
                        {e}
                    </button>
                ))}
            </div>

            <div className="hairline bg-white rounded overflow-hidden">
                <table className="editorial w-full" data-testid="balance-sheet-table">
                    <thead>
                    <tr>
                        <th>Entity</th>
                        <th>Asset</th>
                        <th className="num">Value</th>
                        <th>Liquidity</th>
                        <th>Notes</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map(i => (
                        <tr key={i.id} data-testid={i.read_only ? `bs-row-derived-${i.id}` : `bs-row-${i.id}`}>
                            <td className="text-[var(--muted)] font-mono text-xs uppercase tracking-widest">{i.entity}</td>
                            <td className="font-medium">
                                {i.asset}
                                {i.source === "smsf_page" && (
                                    <span className="ml-2 text-xs" style={{ color: "var(--eucalyptus)" }}
                                          title="Managed on the SMSF page — edits made there flow through automatically."
                                          data-testid="bs-badge-smsf-linked">
                                        ● live from SMSF
                                    </span>
                                )}
                                {!i.source && i.last_live_sync && (
                                    <span className="ml-2 text-xs" style={{ color: "var(--eucalyptus)" }}
                                          title={`Last synced from live: ${i.last_live_sync}`}>
                                        ● live
                                    </span>
                                )}
                            </td>
                            <td className="num font-mono-num">{AUD(i.value)}</td>
                            <td><span className={`chip ${CHIPS[i.liquidity] || ""}`}>{i.liquidity}</span></td>
                            <td className="text-[var(--muted)] text-sm">{i.notes}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {preview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                     style={{ background: "rgba(0,0,0,0.5)" }}
                     data-testid="bs-sync-preview-modal">
                    <div className="bg-white rounded max-w-4xl w-full max-h-[90vh] overflow-auto shadow-xl">
                        <div className="p-6 border-b flex items-start justify-between"
                             style={{ borderColor: "var(--line)" }}>
                            <div>
                                <div className="label-eyebrow">Sync preview</div>
                                <h2 className="font-serif text-2xl mt-1">
                                    {preview.count} portfolio{preview.count === 1 ? "" : "s"} will be updated
                                </h2>
                                <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                                    This overwrites the baseline value with the live value
                                    (tickers + cash, USD converted at AUDUSD {preview.fx_aud_usd?.toFixed(4)}).
                                    Review and confirm.
                                </p>
                            </div>
                            <button onClick={() => setPreview(null)}
                                    className="text-[var(--muted)] hover:text-[var(--ink)]"
                                    data-testid="bs-sync-close">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 overflow-x-auto">
                            <table className="editorial w-full text-sm">
                                <thead>
                                    <tr>
                                        <th>Entity</th>
                                        <th>Portfolio</th>
                                        <th className="num">Baseline</th>
                                        <th className="num">Live tickers</th>
                                        <th className="num">Live cash</th>
                                        <th className="num">New value</th>
                                        <th className="num">Delta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.changes.map((c, i) => (
                                        <tr key={i} data-testid={`bs-sync-row-${i}`}>
                                            <td className="text-[var(--muted)] font-mono text-xs uppercase">{c.entity}</td>
                                            <td className="font-medium">{c.portfolio}</td>
                                            <td className="num font-mono-num text-[var(--muted)]">{AUD(c.workbook)}</td>
                                            <td className="num font-mono-num">{c.live_tickers > 0 ? AUD(c.live_tickers) : "—"}</td>
                                            <td className="num font-mono-num">{c.live_cash > 0 ? AUD(c.live_cash) : "—"}</td>
                                            <td className="num font-mono-num font-medium">{AUD(c.live_value)}</td>
                                            <td className="num font-mono-num"
                                                style={{ color: c.delta > 0 ? "var(--eucalyptus)" : "var(--terracotta)" }}>
                                                {c.delta >= 0 ? "+" : ""}{AUD(c.delta)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-6 border-t flex justify-end gap-2" style={{ borderColor: "var(--line)" }}>
                            <button onClick={() => setPreview(null)} className="btn-ghost text-sm"
                                    data-testid="bs-sync-cancel">Cancel</button>
                            <button onClick={applySync} disabled={busy}
                                    className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                    data-testid="bs-sync-confirm">
                                <Check size={14} />
                                {busy ? "Applying…" : `Apply ${preview.count} update${preview.count === 1 ? "" : "s"}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
